/** How the SPA names and picks a session's output language.
 *
 *  The output language is a registry code (``session.language``); the
 *  list, names and short forms all come from the registry, so a new
 *  output language needs no change here. */
import { localizedLanguageName } from "@/components/language/languageOptions";
import { intlLocale } from "@/i18n/format";
import { entry, outputCodes, resolveCode } from "@/i18n/languages";

/** The language's own name ("Suomi"), or the code when unknown: the
 *  label of a language on its own, as in the picker. */
export function outputLanguageLabel(code: string): string {
  return entry(code)?.endonym ?? code;
}

/** The language's name in the reader's UI language ("engelska" in a
 *  Swedish UI, "Englisch" in German), for use inside a sentence, where
 *  the endonym read as untranslated English. The English name when the
 *  runtime can't name it. */
export function outputLanguageName(code: string, uiLanguage?: string): string {
  return (
    localizedLanguageName(code, intlLocale(uiLanguage)) ?? entry(code)?.englishName ?? code
  );
}

/** The compact form for chips and meta lines ("FI", "简体"). */
export function outputLanguageShort(code: string): string {
  return entry(code)?.short ?? code.toUpperCase();
}

/** The output language a session created from this UI language gets.
 *  Every UI language is an output language; anything else (the dev
 *  pseudo-locale) maps through the aliases, then to English. */
export function outputLanguageForUi(uiCode: string | null | undefined): string {
  return resolveCode(uiCode, outputCodes()) ?? "en";
}

/** What "Follow UI language" resolves to for a session, mirroring the
 *  backend (``language.follow_ui_language``): the session owner's saved
 *  UI language, else this viewer's UI language (the language their
 *  messages would use), else English. */
export function followUiLanguage(
  ownerUiLanguage: string | null | undefined,
  viewerUiLanguage: string | null | undefined,
): string {
  const allowed = outputCodes();
  return (
    resolveCode(ownerUiLanguage, allowed) ?? resolveCode(viewerUiLanguage, allowed) ?? "en"
  );
}

/** The language a session's output is in: its own language, or what
 *  "Follow UI language" resolves to. */
export function sessionOutputLanguage(
  session: { language: string | null; owner?: { ui_language?: string | null } | null },
  viewerUiLanguage: string | null | undefined,
): string {
  return (
    resolveCode(session.language, outputCodes()) ??
    followUiLanguage(session.owner?.ui_language, viewerUiLanguage)
  );
}

